"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { editEventMemberSchema } from "@/app/(main)/(club)/mes-clubs/[id]/components/modal/event/absent/schema";
import { useFlashMessage } from "@/app/(main)/(club)/mes-clubs/[id]/components/use-flash-message";
import { FALLBACK_REASONS } from "@/app/(main)/(club)/mes-clubs/[id]/utils/fallback-reasons";
import { FormActions } from "@/app/(security)/inscription/components/form-actions";
import FormPartTitle from "@/components/form/form-part-title";
import { FormSelect } from "@/components/global/form/inputs/form-select";
import { ModalSkeleton } from "@/components/modal/modal-skeleton";
import { useAbsenceMutation } from "@/mutations/use-absence-mutation";
import { useEventQuery } from "@/queries/event/event-queries";
import { useReasonQuery } from "@/queries/reason-for-refusal/use-reason-query";
import { useModalStore } from "@/stores/modal-store";
import { EventEnum } from "@/TS/Enums/global_enums";
import { OtherDateTitle } from "../invitations/modal/participation/title";
import { Header } from "./modal/event/absent/header";
import { ModalRedAlert } from "./modal/event/absent/modal-red-alert";
import { ReasonsError } from "./modal/event/absent/reasons-error";
interface EventProps {
  type?: EventEnum;
  clubSlug?: string;
  startDate?: string;
}
interface EditEventModalContentProps {
  eventProps: EventProps;
  handleFetch: () => Promise<unknown>;
  setShouldRefresh?: (value: boolean) => void;
}
export interface EditEventFormData {
  motifAbsence: string;
}
export default function EditEventModalContentMember({
  eventProps,
  handleFetch,
  setShouldRefresh
}: EditEventModalContentProps) {
  const {
    modalConfig,
    close
  } = useModalStore();
  const {
    showFlashMessage
  } = useFlashMessage();
  const skeletonOptions = modalConfig?.modalOptions?.loader?.skeleton;
  const loaderComponent = modalConfig?.modalOptions?.loader?.component;
  const {
    data: event,
    isLoading: isEventLoading
  } = useEventQuery({
    type: eventProps.type,
    clubSlug: eventProps.clubSlug,
    startDate: eventProps.startDate?.split("T")[0]
  });
  const {
    data: reasonsData,
    isLoading: isReasonsLoading,
    error: reasonsError
  } = useReasonQuery("event_absence");
  const methods = useForm<EditEventFormData>({
    mode: "onSubmit",
    resolver: zodResolver(editEventMemberSchema),
    defaultValues: {
      motifAbsence: ""
    }
  });
  const {
    handleSubmit,
    formState: {
      errors,
      isSubmitted,
      isDirty
    }
  } = methods;
  const absenceMutation = useAbsenceMutation(handleFetch, setShouldRefresh);
  const onSubmit = async (data: EditEventFormData) => {
    try {
      if (!isDirty) {
        showFlashMessage("Aucune modification apportée", "warning2", "mes-clubs-calendar");
        close();
        return;
      }
      await absenceMutation.mutateAsync({
        data: {
          reason: data.motifAbsence
        },
        type: eventProps.type!,
        clubSlug: eventProps.clubSlug!,
        startDate: eventProps.startDate?.split("T")[0]!
      });
      close();
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
    }
  };
  const reasons = reasonsData?.["hydra:member"] || (reasonsError ? FALLBACK_REASONS : []);
  if (isEventLoading) {
    if (!skeletonOptions) {
      return <div className="w-full h-[200px] flex items-center justify-center transition-[max-height] duration-300 ease-in-out max-h-[200px]">
          {loaderComponent as ReactNode}
        </div>;
    }
    return <ModalSkeleton className={skeletonOptions?.className} header={skeletonOptions?.header} title={skeletonOptions?.title} content={skeletonOptions?.content} />;
  }
  return <div className="transition-[max-height] duration-300 ease-in-out max-h-[1000px]">
      <div className="p-6">
        <OtherDateTitle title="Gérer mon absence" />

        <ModalRedAlert />

        <Header event={event} title="Ordre du jour" />

        <FormPartTitle className="mb-0" classNameTitle="!text-base tracking-[.8px]">
          VOTRE ABSENCE
        </FormPartTitle>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex flex-col pt-4 pb-8 rounded-lg bg-opacity-5">
              <div className="relative w-full md:w-1/2">
                <div className="relative">
                  <Controller name="motifAbsence" control={methods.control} render={({
                  field
                }) => <FormSelect {...field} label="Motif d'absence" error={errors.motifAbsence?.message} isValid={isSubmitted && !errors.motifAbsence} disabled={isReasonsLoading} required styles={{
                  wrapper: {
                    className: "w-full"
                  }
                }}>
                        <option value="">Sélectionner un motif...</option>
                        {reasons.map(reason => <option key={reason.key} value={reason["@id"]}>
                            {reason.value}
                          </option>)}
                      </FormSelect>} />
                </div>

                {reasonsError && reasons.length === 0 && <ReasonsError content="Impossible de charger les motifs d'absence." />}
              </div>
            </div>
            <FormActions isPending={absenceMutation.isPending} closeModal={close} />
          </form>
        </FormProvider>
      </div>
    </div>;
}